import React from 'react';
import { useIntl } from 'react-intl';
import { Form, Input } from 'semantic-ui-react';
import fields, { globalFieldLengths } from '@/constants/fields';
import FormValidationError from '@/components/errors/FormValidationError';
import { states } from '@/constants/lifecycle';
import {
  firstError, firstResponseError, translatedError, checkRequired, checkMaxLength, validationClasses,
} from '@/utils/errors';
import Dialog from '@/components/dialogs/Dialog';
import Selectors from '../hooks/selectors';
import Events from '../hooks/events';

const SAFE_FOLDER_NAME = /^[a-zA-Z0-9]([a-zA-Z0-9 ._-]*[a-zA-Z0-9])?$/;
const CONSECUTIVE_DOTS = /\.\./;
const TUS_ID_PATTERN = /^[a-f0-9]{32}$/;

const WINDOWS_RESERVED_NAMES = new Set([
  'CON', 'PRN', 'AUX', 'NUL',
  'COM0', 'COM1', 'COM2', 'COM3', 'COM4', 'COM5', 'COM6', 'COM7', 'COM8', 'COM9',
  'LPT0', 'LPT1', 'LPT2', 'LPT3', 'LPT4', 'LPT5', 'LPT6', 'LPT7', 'LPT8', 'LPT9',
]);

const ENTITY = 'dataset.folder';
const TITLE = 'dataset.folder.create.validation.name.title';

const isWindowsReservedName = (name) => {
  const baseName = name.split('.')[0];
  return baseName && WINDOWS_RESERVED_NAMES.has(baseName.toUpperCase());
};

const validateFolderName = (name) => {
  const errors = [];
  const trimmed = name.trim();

  checkRequired(trimmed, [fields.NAME], errors, ENTITY, TITLE);

  if (errors.length > 0) {
    return errors;
  }

  checkMaxLength(trimmed, [fields.NAME], errors, ENTITY, TITLE, globalFieldLengths.name);

  if (!SAFE_FOLDER_NAME.test(trimmed)) {
    errors.push(translatedError(ENTITY, TITLE, 'validation.folder.name.invalid.characters', [fields.NAME]));
    return errors;
  }

  if (CONSECUTIVE_DOTS.test(trimmed)) {
    errors.push(translatedError(ENTITY, TITLE, 'validation.folder.name.consecutive.dots', [fields.NAME]));
  }

  if (isWindowsReservedName(trimmed)) {
    errors.push(translatedError(ENTITY, TITLE, 'validation.folder.name.reserved', [fields.NAME]));
  }

  if (TUS_ID_PATTERN.test(trimmed)) {
    errors.push(translatedError(ENTITY, TITLE, 'validation.folder.name.resembles.id', [fields.NAME]));
  }

  return errors;
};

const FolderCreationDialog = ({ theme }) => {
  const intl = useIntl();
  const {
    category, code, saveStatus, saveErrors, name,
  } = Selectors.useDatasetFolderCreationDialog();
  const hide = Events.useHideCreateDatasetFolder();
  const updateProperties = Events.useUpdateCreateDatasetAttachmentFolderProperties();
  const createFolder = Events.useCreateDatasetFolder(category, { code });

  const submitForm = () => {
    const errors = validateFolderName(name);
    updateProperties({ properties: { saveErrors: errors } });

    if (errors.length === 0) {
      createFolder({ folderName: name.trim() });
    }
  };

  const nameError = saveErrors.find((e) => e.fields.includes(fields.NAME));

  return (
    <Dialog
      className="aridhia-dataset-folder-creation-dialog"
      error={firstResponseError(saveErrors)}
      icon="folder"
      inProgress={saveStatus === states.PENDING}
      minimumDataEntryMet={name.trim().length > 0}
      onClose={hide}
      onSave={submitForm}
      onDismiss={() => hide()}
      saveMessageId="save.cohort.create.button"
      theme={theme}
      title={intl.formatMessage({ id: 'dataset.folder.create.title' })}
    >
      <Form autoComplete="off" onSubmit={submitForm} error={firstError(saveErrors) !== null}>
        <FormValidationError
          error={nameError}
          htmlFor="folder_name"
          labelId="dataset.folder.create.name"
        >
          <Input
            id="folder_name"
            autoFocus
            disabled={saveStatus === states.PENDING}
            type="text"
            size="large"
            placeholder={intl.formatMessage({ id: 'dataset.folder.create.name.placeholder' })}
            value={name}
            maxLength={globalFieldLengths.name}
            onChange={(e) => {
              updateProperties({ properties: { name: e.target.value } });
            }}
            className={validationClasses(nameError)}
          />
        </FormValidationError>
      </Form>
    </Dialog>
  );
};

export default FolderCreationDialog;
